import { Alert, Box, Chip, Typography } from '@mui/material';
import { safeDecode } from '../utils/decode';

const Section = ({ title, count, colour = 'default', children }) => (count > 0 ? (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
      <Typography variant="subtitle2">{title}</Typography>
      <Chip size="small" label={count} color={colour} sx={{ height: 20 }} />
    </Box>
    <Box component="ul" sx={{ m: 0, pl: 2.5, typography: 'body2' }}>{children}</Box>
  </Box>
) : null);

// v1.4.0: a quota with skippable questions also has the count with people who skip them.
const withSkips = (count, skips) => (skips == null ? `${count}` : `${count} (${skips} counting people who skip)`);

// Null (v1.5.0): not checked, because Alchemer's completes couldn't be read; the result's warnings say so.
const countCheck = (check) => {
  if (!check) return null;
  if (check.matches_alchemer === null || 'and' in check) {
    return `Alchemer counts ${check.alchemer ?? '?'}; with AND it would be ${withSkips(check.and, check.and_with_skips)}, `
      + `with OR ${withSkips(check.or, check.or_with_skips)}`;
  }
  return `Wrangler counts ${withSkips(check.ours, check.ours_with_skips)}, Alchemer ${check.alchemer ?? '?'}`;
};

const CheckedQuota = ({ quota }) => {
  const counts = countCheck(quota.count_check);
  return <li><strong>{safeDecode(quota.name)}</strong>{counts && <>: {counts}</>}</li>;
};

/** What an import from Alchemer did (Wrangler's ImportResult), problems first. */
const ImportResultView = ({ result }) => {
  if (!result) return <Typography variant="body2">This import left no result.</Typography>;
  const imported = result.imported || [];
  const notConverted = result.not_converted || [];
  const needsChoice = result.needs_join_choice || [];
  const inferred = result.inferred_joins || [];
  const mismatches = result.count_mismatches || [];
  const skipped = result.skipped_already_imported || [];
  const skippable = result.skippable_not_in || [];
  const warnings = result.warnings || [];
  return (
    <Box>
      {/* v1.5.0: problems the import worked around, already in plain English. */}
      {warnings.map((warning) => <Alert key={warning} severity="warning" sx={{ mb: 2 }}>{warning}</Alert>)}
      <Typography variant="body2" sx={{ mb: 2 }}>
        Imported <strong>{imported.length}</strong> quota{imported.length !== 1 ? 's' : ''}, each OFF and marked
        Needs review. Alchemer&apos;s own quotas weren&apos;t changed.
        {skipped.length > 0 && <> {skipped.length} {skipped.length !== 1 ? 'were' : 'was'} already imported.</>}
      </Typography>
      {result.needs_refresh && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Completes recorded before now don&apos;t have answers for every question these quotas use. Run
          &ldquo;Refresh from Alchemer&rdquo; on the Setup tab to count them correctly.
        </Alert>
      )}
      <Section title="Couldn't be copied: build these by hand" count={notConverted.length} colour="error">
        {notConverted.map((q) => (
          <li key={q.alchemer_quota_id}><strong>{safeDecode(q.name)}</strong>: {q.problems.join('; ')}</li>
        ))}
      </Section>
      <Section title="Need an AND / OR choice before they can be switched ON" count={needsChoice.length} colour="warning">
        {needsChoice.map((q) => <CheckedQuota key={q.quota_id} quota={q} />)}
      </Section>
      <Section title="Counts that differ from Alchemer's" count={mismatches.length} colour="warning">
        {mismatches.map((q) => <CheckedQuota key={q.quota_id} quota={q} />)}
      </Section>
      <Section title="Count people who skip? Answer in each quota's panel" count={skippable.length} colour="warning">
        {skippable.map((q) => (
          <li key={q.quota_id}>
            <strong>{safeDecode(q.name)}</strong>: an &ldquo;is not one of&rdquo; condition on a question that isn&apos;t
            required (or may not be), imported as &ldquo;answered, and not one of&rdquo;
          </li>
        ))}
      </Section>
      <Section title="AND / OR worked out from Alchemer's counts" count={inferred.length}>
        {inferred.map((q) => (
          <li key={q.quota_id}>
            <strong>{safeDecode(q.name)}</strong>: groups joined by {String(q.group_join).toUpperCase()}
            {q.group_join_note ? ` (${q.group_join_note})` : ''}
          </li>
        ))}
      </Section>
      <Section title="Imported" count={imported.length}>
        {imported.map((q) => (
          <li key={q.quota_id}>{safeDecode(q.name)}, limit {q.limit}</li>
        ))}
      </Section>
      <Section title="Skipped: already imported" count={skipped.length}>
        {skipped.map((q) => <li key={q.alchemer_quota_id}>{safeDecode(q.name || `Alchemer quota ${q.alchemer_quota_id}`)}</li>)}
      </Section>
    </Box>
  );
};

export default ImportResultView;
